import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, RefreshCw, Activity, Zap } from 'lucide-react';
import { checkAllAgents } from '../utils/healthCheck';
import ModernAIService from '../services/modernAIService';

const HealthStatus = () => {
  const [agentStatus, setAgentStatus] = useState([]);
  const [serviceStatus, setServiceStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const modernAIService = new ModernAIService();

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const checkHealth = async () => {
    try {
      const results = await checkAllAgents();
      setAgentStatus(results);
      
      // Check AI services status
      const aiServiceStatus = modernAIService.getServiceStatus();
      setServiceStatus(aiServiceStatus);
    } catch (error) {
      console.error('Health check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'unhealthy':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600';
      case 'unhealthy':
        return 'text-red-600';
      default:
        return 'text-yellow-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
        <span>Checking system...</span>
        <RefreshCw className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  const healthyAgents = agentStatus.filter(agent => agent.status === 'healthy').length;
  const totalAgents = agentStatus.length;
  
  // Count healthy AI services
  const aiServices = Object.keys(serviceStatus);
  const healthyAIServices = aiServices.filter(service => serviceStatus[service]?.available).length;

  const overallStatus = healthyAgents === totalAgents && healthyAIServices === aiServices.length ? 'healthy' : 'unhealthy';

  return (
    <div className="relative">
      {/* Main Status Display */}
      <div 
        className="flex items-center space-x-3 cursor-pointer group"
        onClick={() => setShowDetails(!showDetails)}
      >
        {/* Overall Status Indicator */}
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full transition-all duration-200 ${
            overallStatus === 'healthy' ? 'bg-green-500 animate-pulse' : 'bg-red-500'
          }`}></div>
          <span className="text-sm font-medium text-gray-700">System Status</span>
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Activity className="w-4 h-4 text-blue-500" />
            <span className={`text-sm font-medium ${
              healthyAgents === totalAgents ? 'text-green-600' : 'text-red-600'
            }`}>
              {healthyAgents}/{totalAgents}
            </span>
          </div>
          
          <div className="flex items-center space-x-1">
            <Zap className="w-4 h-4 text-purple-500" />
            <span className={`text-sm font-medium ${
              healthyAIServices === aiServices.length ? 'text-green-600' : 'text-red-600'
            }`}>
              {healthyAIServices}/{aiServices.length}
            </span>
          </div>
        </div>
        
        {/* Refresh Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            checkHealth();
          }}
          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-110"
          title="Refresh status"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      {/* Detailed Status Dropdown */}
      {showDetails && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-50">
          <div className="space-y-4">
            {/* Agents Section */}
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Activity className="w-4 h-4 mr-2 text-blue-500" />
                Agents ({healthyAgents}/{totalAgents})
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {agentStatus.map((agent, index) => (
                  <div 
                    key={agent.agent || index} 
                    className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg"
                    title={`${agent.agent}: ${agent.status}${agent.error ? ` - ${agent.error}` : ''}`}
                  >
                    {getStatusIcon(agent.status)}
                    <span className={`text-xs font-medium capitalize ${getStatusColor(agent.status)}`}>
                      {agent.agent}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* AI Services Section */}
            {aiServices.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
                  <Zap className="w-4 h-4 mr-2 text-purple-500" />
                  AI Services ({healthyAIServices}/{aiServices.length})
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {aiServices.map((service) => {
                    const isAvailable = serviceStatus[service]?.available;
                    const status = isAvailable ? 'healthy' : 'unhealthy';
                    return (
                      <div 
                        key={service} 
                        className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg"
                        title={`${service}: ${status} - ${serviceStatus[service]?.features?.join(', ') || 'No features'}`}
                      >
                        {getStatusIcon(status)}
                        <span className={`text-xs font-medium capitalize ${getStatusColor(status)}`}>
                          {service}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Last Updated */}
            <div className="pt-2 border-t border-gray-200">
              <span className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthStatus;